import { Button, Card, Stack, Typography } from '@mui/joy';
import QuestionBubbleIcon from 'jsx:../../assets/icons/question-square-bubble.svg';

import { usePermissionsContext } from '../../context/permissionsContext';
import clearfeed from '../../services/support/clearfeed';

import { DEFAULT_CTA_LABEL, DEFAULT_SUBTITLE, DEFAULT_TITLE, VARIANTS } from './constants';
import type { AskAQuestionCardProps } from './types';

const AskAQuestionCard: React.FC<AskAQuestionCardProps> = ({
  prefill,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  ctaLabel = DEFAULT_CTA_LABEL,
  width,
  variant = 'default',
}) => {
  // Per the Figma note on frame 4:2 — question prompts are paid-only.
  const { isFreemiumUser } = usePermissionsContext();
  if (isFreemiumUser) return null;

  const style = VARIANTS[variant];

  return (
    <Card
      variant={style.container.variant}
      sx={{
        p: style.container.padding,
        gap: style.outerGap,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: (theme) => theme.radius.sm,
        ...(style.container.variant === 'plain' && {
          background: 'transparent',
          boxShadow: 'none',
        }),
        ...(width !== undefined ? { width, maxWidth: '100%', flexShrink: 0 } : { width: '100%' }),
      }}
    >
      {style.icon.show && (
        <QuestionBubbleIcon
          width={style.icon.size}
          height={style.icon.size}
          style={{ flexShrink: 0 }}
        />
      )}
      <Stack
        direction={style.text.direction}
        alignItems={style.text.alignItems}
        gap={style.text.innerGap}
        flex={1}
      >
        <Typography
          level={style.title.level}
          fontWeight={'xl'}
          sx={{ color: (theme) => theme.palette.text.primary }}
        >
          {title}
        </Typography>
        <Typography
          fontSize={style.subtitle.fontSize}
          fontWeight={style.subtitle.fontWeight}
          lineHeight={1.42}
          noWrap={style.subtitle.noWrap}
          sx={{ color: (theme) => theme.palette.text.tertiary }}
        >
          {subtitle}
        </Typography>
      </Stack>
      <Button
        variant={style.button.variant}
        color={style.button.color}
        size='sm'
        onClick={() => clearfeed.open(prefill)}
        sx={{
          textWrap: 'nowrap',
          height: 32,
          borderRadius: (theme) => theme.radius.sm,
          fontWeight: 'md',
        }}
      >
        {ctaLabel}
      </Button>
    </Card>
  );
};

export { AskAQuestionCard };
export type { AskAQuestionVariant } from './types';
